import { useEffect, useRef } from "react";

// Minimum time tab must be hidden before triggering refetch on return
const TAB_HIDDEN_THRESHOLD_MS = 30000; // 30 seconds

interface UseVisibilityRefetchOptions {
  currentConversationId: string | undefined | null;
  onRefetch: (conversationId: string) => void;
}

/**
 * Hook to refetch the current conversation when the user returns to the tab
 * after being away for more than 30 seconds.
 *
 * This handles cases where:
 * - The subscription may have silently disconnected while tab was inactive
 * - Messages arrived while the user was on another tab
 * - Browser may have throttled WebSocket connections while tab was hidden
 */
export function useVisibilityRefetch({
  currentConversationId,
  onRefetch,
}: UseVisibilityRefetchOptions) {
  const hiddenAtRef = useRef<number | null>(null);
  const currentIdRef = useRef(currentConversationId);

  // Keep ref in sync
  useEffect(() => {
    currentIdRef.current = currentConversationId;
  }, [currentConversationId]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        hiddenAtRef.current = Date.now();
      } else {
        if (hiddenAtRef.current !== null) {
          const hiddenDuration = Date.now() - hiddenAtRef.current;
          if (hiddenDuration >= TAB_HIDDEN_THRESHOLD_MS && currentIdRef.current) {
            console.log(
              `[useVisibilityRefetch] Tab was hidden for ${Math.round(hiddenDuration / 1000)}s, refetching conversation`
            );
            onRefetch(currentIdRef.current);
          }
          hiddenAtRef.current = null;
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [onRefetch]);
}
